import React, { useState, useCallback } from "react";
import "./App.css";

// ✅ PUT YOUR PUBLIC AWS/CloudFront LOGO URL HERE
const AWS_LOGO_URL = "https://analyzeoffer-public-assets.s3.us-east-1.amazonaws.com/badges/logo-final.png";

// ✅ API route handles Make.com webhook (avoids CORS)
const ANALYZE_API_URL = "/api/analyze";

// ✅ SAMPLE REPORT URLs - UPDATE THESE WITH YOUR S3/CloudFront URLs
const SAMPLE_REPORTS = {
  vehicle: "https://your-bucket.s3.amazonaws.com/samples/vehicle-analysis-sample.pdf",
  utility: "https://your-bucket.s3.amazonaws.com/samples/utility-bill-sample.pdf",
  solar: "https://your-bucket.s3.amazonaws.com/samples/solar-contract-sample.pdf",
};

const STRIPE_SINGLE = "https://buy.stripe.com/eVq9AT94vfNa13P7NlbbG00";
const STRIPE_ANNUAL = "https://buy.stripe.com/4gMbJ15Sj6cAfYJaZxbbG02";
const STRIPE_MONTHLY = "https://buy.stripe.com/bJe00jcgH30oeUF9VtbbG01";

// Document types
const DOCUMENT_TYPES = [
  "Vehicle Purchase or Lease",
  "Vehicle Loan or Financing",
  "Vehicle Repair/Service Estimate",
  "Reverse Mortgage",
  "Home Mortgage or Refinance",
  "Personal Loan",
  "Home Improvement Contract",
  "Real Estate Purchase Contract",
  "Rental/Lease Agreement",
  "Contractor Agreement",
  "Bill Analysis (Medical, Utility, Phone, Cable, HOA)",
  "Insurance Policy",
  "Service/Subscription Agreement",
  "Timeshare Contract",
  "Other Document or Contract",
];

export default function App() {
  const [logoOk, setLogoOk] = useState(true);
  const [currentPage, setCurrentPage] = useState("home");

  const showAwsLogo =
    logoOk &&
    AWS_LOGO_URL &&
    AWS_LOGO_URL !== "PASTE_YOUR_PUBLIC_AWS_LOGO_URL_HERE";

  // Show Upload page
  if (currentPage === "upload") {
    return (
      <UploadPage
        onBack={() => setCurrentPage("home")}
        showAwsLogo={showAwsLogo}
        setLogoOk={setLogoOk}
      />
    );
  }

  // Show Landing page
  return (
    <>
      {/* Navigation */}
      <nav>
        <div className="nav-container">
          <a href="#top" className="logo">
            <div className="logo-icon">
              {showAwsLogo ? (
                <img
                  src={AWS_LOGO_URL}
                  alt="AnalyzeOffer.ai"
                  onError={() => setLogoOk(false)}
                />
              ) : (
                <NavLogoSVG />
              )}
            </div>
            <span className="logo-text">
              AnalyzeOffer<span>.ai</span>
            </span>
          </a>

          <div className="nav-links">
            <a href="#how-it-works">How It Works</a>
            <a href="#pricing">Pricing</a>
            <button
              onClick={() => setCurrentPage("upload")}
              className="nav-cta"
            >
              Get Started
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Background */}
      <div className="hero-bg" id="top">
        <div className="grid-overlay" />
      </div>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <div className="badge">
            <span className="badge-dot" />
            AI-Powered Analysis
          </div>

          <h1>
            Know More.
            <br />
            <span>Pay Less.</span>
          </h1>

          <p>
            AI-powered contract and bill analysis that spots hidden fees, unfair
            terms, and overcharges in seconds. Protect yourself before you sign.
          </p>

          <div className="hero-buttons">
            <button
              onClick={() => setCurrentPage("upload")}
              className="btn btn-primary"
            >
              <UploadIcon />
              Analyze Your Document
            </button>
            <a href="#how-it-works" className="btn btn-secondary">
              See How It Works
            </a>
          </div>

          {/* Trust Bar */}
          <div className="trust-bar">
            <div className="trust-item">
              <div className="trust-number">$2.3M+</div>
              <div className="trust-label">Saved for Customers</div>
            </div>
            <div className="trust-item">
              <div className="trust-number">15K+</div>
              <div className="trust-label">Documents Analyzed</div>
            </div>
            <div className="trust-item">
              <div className="trust-number">97%</div>
              <div className="trust-label">Accuracy Rate</div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="how-it-works" id="how-it-works">
        <div className="section-header">
          <h2>How It Works</h2>
          <p>Get expert analysis in three simple steps. No lawyers needed.</p>
        </div>

        <div className="steps-grid">
          <div className="step-card">
            <div className="step-number">1</div>
            <h3>Upload Your Document</h3>
            <p>
              Simply upload your contract, bill, or offer letter. We accept
              PDFs, images, and most document formats.
            </p>
          </div>
          <div className="step-card">
            <div className="step-number">2</div>
            <h3>AI Analyzes Everything</h3>
            <p>
              Our AI scans every clause, fee, and term—comparing against
              thousands of similar documents.
            </p>
          </div>
          <div className="step-card">
            <div className="step-number">3</div>
            <h3>Get Your Report</h3>
            <p>
              Receive a clear, actionable report with red flags, negotiation
              tips, and money-saving insights.
            </p>
          </div>
        </div>
      </section>

      {/* Sample Reports Section */}
      <section className="sample-reports" id="samples">
        <div className="section-header">
          <h2>See What You'll Get</h2>
          <p>View real sample reports before you buy. No surprises.</p>
        </div>

        <div className="samples-grid">
          <div className="sample-card">
            <div className="sample-icon">🚗</div>
            <h3>Vehicle Purchase</h3>
            <p>See how we expose hidden dealer fees, inflated add-ons, and unfair financing terms.</p>
            <a href={SAMPLE_REPORTS.vehicle} target="_blank" rel="noreferrer" className="sample-link">
              <DocumentIcon /> View Sample Report
            </a>
          </div>
          <div className="sample-card">
            <div className="sample-icon">💡</div>
            <h3>Utility Bill Dispute</h3>
            <p>See how we caught a $794 overcharge from an estimated meter read.</p>
            <a href={SAMPLE_REPORTS.utility} target="_blank" rel="noreferrer" className="sample-link">
              <DocumentIcon /> View Sample Report
            </a>
          </div>
          <div className="sample-card">
            <div className="sample-icon">☀️</div>
            <h3>Solar Contract</h3>
            <p>See how we identified 7 red flags and $20,000+ in hidden costs.</p>
            <a href={SAMPLE_REPORTS.solar} target="_blank" rel="noreferrer" className="sample-link">
              <DocumentIcon /> View Sample Report
            </a>
          </div>
        </div>
      </section>

      {/* Document Types */}
      <section className="doc-types">
        <div className="section-header">
          <h2>What We Analyze</h2>
          <p>From car deals to cell phone contracts, we've got you covered.</p>
        </div>

        <div className="doc-pills">
          <span className="doc-pill popular">Vehicle Purchase or Lease</span>
          <span className="doc-pill popular">Vehicle Loan or Financing</span>
          <span className="doc-pill popular">Vehicle Repair/Service Estimate</span>
          <span className="doc-pill popular">Reverse Mortgage</span>
          <span className="doc-pill popular">Home Mortgage or Refinance</span>
          <span className="doc-pill popular">Personal Loan</span>
          <span className="doc-pill popular">Home Improvement Contract</span>
          <span className="doc-pill popular">Real Estate Purchase Contract</span>
          <span className="doc-pill popular">Rental/Lease Agreement</span>
          <span className="doc-pill popular">Contractor Agreement</span>
          <span className="doc-pill popular">Bill Analysis (Medical, Utility, Phone, Cable, HOA)</span>
          <span className="doc-pill popular">Insurance Policy</span>
          <span className="doc-pill popular">Service/Subscription Agreement</span>
          <span className="doc-pill popular">Timeshare Contract</span>
          <span className="doc-pill popular">Other Document or Contract</span>
        </div>
      </section>

      {/* Pricing */}
      <section className="pricing" id="pricing">
        <div className="section-header">
          <h2>Simple, Transparent Pricing</h2>
          <p>Pay only for what you need. No hidden fees—ever.</p>
        </div>

        <div className="pricing-grid three-col">
          <div className="pricing-card">
            <h3>Single Report</h3>
            <div className="price">$49</div>
            <div className="description">One document, one report</div>
            <div className="price-per">$49.00 per analysis</div>
            <ul>
              <li>
                <CheckIcon /> Comprehensive analysis
              </li>
              <li>
                <CheckIcon /> Red flag identification
              </li>
              <li>
                <CheckIcon /> Negotiation scripts
              </li>
              <li>
                <CheckIcon /> No subscription required
              </li>
            </ul>
            <a
              href={STRIPE_SINGLE}
              className="btn btn-primary"
              rel="noreferrer"
            >
              Get Started
            </a>
          </div>

          <div className="pricing-card featured">
            <span className="pricing-badge">Best Value</span>
            <h3>Annual Protection</h3>
            <div className="price">
              $99<span>/year</span>
            </div>
            <div className="description">Up to 15 analyses per month</div>
            <div className="price-per">As low as $0.55 per analysis</div>
            <ul>
              <li>
                <CheckIcon /> 15 analyses/month (180/year)
              </li>
              <li>
                <CheckIcon /> Save $80+ vs monthly
              </li>
              <li>
                <CheckIcon /> Priority processing
              </li>
              <li>
                <CheckIcon /> Usage resets monthly
              </li>
            </ul>
            <div className="pricing-note">Billed annually • Best for families & investors</div>
            <a
              href={STRIPE_ANNUAL}
              className="btn btn-primary"
              rel="noreferrer"
            >
              Get Started
            </a>
          </div>

          <div className="pricing-card">
            <h3>Monthly Protection</h3>
            <div className="price">
              $14.99<span>/mo</span>
            </div>
            <div className="description">Up to 10 analyses per month</div>
            <div className="price-per">As low as $1.50 per analysis</div>
            <ul>
              <li>
                <CheckIcon /> 10 analyses/month
              </li>
              <li>
                <CheckIcon /> Instant results
              </li>
              <li>
                <CheckIcon /> Cancel after 2 months
              </li>
              <li>
                <CheckIcon /> $179.88/year if kept
              </li>
            </ul>
            <div className="pricing-note">2-month minimum • Usage resets monthly</div>
            <a
              href={STRIPE_MONTHLY}
              className="btn btn-primary"
              rel="noreferrer"
            >
              Subscribe
            </a>
          </div>
        </div>

        <div className="pricing-comparison">
          <p>💡 <strong>Why Annual?</strong> At just $99/year, you get 180 analyses vs 120 with monthly ($179.88/year). That's 50% more coverage for 45% less cost.</p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-content">
          <h2>Powered by AI. Built for Savings.</h2>
          <p>
            Join thousands who've saved money by analyzing before committing.
          </p>
          <button
            onClick={() => setCurrentPage("upload")}
            className="btn btn-primary"
          >
            <UploadIcon />
            Analyze Your First Document
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer>
        <div className="footer-logo">
          <div className="logo-icon">
            {showAwsLogo ? (
              <img
                src={AWS_LOGO_URL}
                alt="AnalyzeOffer.ai"
                onError={() => setLogoOk(false)}
              />
            ) : (
              <FooterLogoSVG />
            )}
          </div>
          <span className="logo-text">
            AnalyzeOffer<span>.ai</span>
          </span>
        </div>

        <div className="footer-links">
          <a href="#samples">Sample Reports</a>
          <a href="#pricing">Pricing</a>
          <a href="#privacy">Privacy Policy</a>
          <a href="#tos">Terms of Service</a>
        </div>

        <div className="footer-disclaimer">
          AnalyzeOffer.ai provides AI-powered analysis to support personal decision-making.
          Results are informational and not legal, financial, or professional advice.
        </div>

        <div className="footer-copy">
          © 2026 AnalyzeOffer.ai — Know More. Pay Less.
        </div>
      </footer>

      {/* Additional Styles for new sections */}
      <style>{`
        .sample-reports {
          padding: 5rem 2rem;
          background: #0f172a;
          position: relative;
        }

        .sample-reports .section-header h2 {
          color: #f8fafc;
        }

        .sample-reports .section-header p {
          color: #94a3b8;
        }

        .samples-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 2rem;
          max-width: 1000px;
          margin: 0 auto;
        }

        .sample-card {
          background: rgba(30, 41, 59, 0.8);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          padding: 2rem;
          text-align: center;
          transition: all 0.3s ease;
        }

        .sample-card:hover {
          border-color: rgba(74, 222, 128, 0.4);
          transform: translateY(-4px);
          background: rgba(30, 41, 59, 1);
        }

        .sample-icon {
          font-size: 3rem;
          margin-bottom: 1rem;
        }

        .sample-card h3 {
          color: #f8fafc;
          font-size: 1.25rem;
          margin: 0 0 0.75rem 0;
        }

        .sample-card p {
          color: #cbd5e1;
          font-size: 0.9rem;
          margin: 0 0 1.5rem 0;
          line-height: 1.5;
        }

        .sample-link {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          color: #4ade80;
          font-weight: 500;
          text-decoration: none;
          padding: 0.75rem 1.25rem;
          border: 1px solid rgba(74, 222, 128, 0.4);
          border-radius: 8px;
          transition: all 0.2s ease;
        }

        .sample-link:hover {
          background: rgba(74, 222, 128, 0.15);
          border-color: #4ade80;
        }

        .sample-link svg {
          width: 18px;
          height: 18px;
        }

        .price-per {
          color: #4ade80;
          font-size: 0.85rem;
          font-weight: 500;
          margin-top: 0.5rem;
          margin-bottom: 1rem;
        }

        .pricing-comparison {
          max-width: 700px;
          margin: 2rem auto 0;
          padding: 1.25rem 1.5rem;
          background: rgba(74, 222, 128, 0.08);
          border: 1px solid rgba(74, 222, 128, 0.2);
          border-radius: 12px;
          text-align: center;
        }

        .pricing-comparison p {
          color: #e2e8f0;
          font-size: 0.95rem;
          margin: 0;
        }

        .footer-disclaimer {
          color: #64748b;
          font-size: 0.8rem;
          max-width: 600px;
          margin: 1rem auto;
          text-align: center;
          line-height: 1.5;
        }
      `}</style>
    </>
  );
}

/* ========== UPLOAD PAGE COMPONENT ========== */

function UploadPage({ onBack, showAwsLogo, setLogoOk }) {
  const [files, setFiles] = useState([]);
  const [documentType, setDocumentType] = useState("");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const newFiles = Array.from(e.dataTransfer.files);
      setFiles(prev => [...prev, ...newFiles].slice(0, 10)); // Max 10 files
    }
  }, []);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      setFiles(prev => [...prev, ...newFiles].slice(0, 10)); // Max 10 files
    }
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (files.length === 0 || !documentType || !fullName || !email) {
      setSubmitStatus({
        type: "error",
        message: "Please fill in all required fields and upload at least one file",
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Step 1: Upload each file to S3 via /api/upload
      const uploadedFiles = [];
      
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        
        const uploadResponse = await fetch("/api/upload", {
          method: "POST",
          body: formData,
        });
        
        if (!uploadResponse.ok) {
          throw new Error(`Failed to upload ${file.name}`);
        }
        
        const uploadResult = await uploadResponse.json();
        uploadedFiles.push({
          fileName: uploadResult.fileName,
          bucket: uploadResult.bucket,
          key: uploadResult.key,
          mimeType: uploadResult.mimeType,
        });
      }

      // Step 2: Send file references to Make.com via /api/analyze
      const response = await fetch(ANALYZE_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          files: uploadedFiles,
          documentType,
          fullName,
          email,
          notes,
          submittedAt: new Date().toISOString(),
          fileCount: uploadedFiles.length,
        }),
      });

      if (response.ok) {
        const fileWord = files.length === 1 ? "document" : "documents";
        setSubmitStatus({
          type: "success",
          message: `${files.length} ${fileWord} submitted! Check your email for the analysis report(s) in 2-3 minutes.`,
        });
        setFiles([]);
        setDocumentType("");
        setFullName("");
        setEmail("");
        setNotes("");
      } else {
        throw new Error("Submission failed");
      }
    } catch (error) {
      setSubmitStatus({
        type: "error",
        message: "Something went wrong. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="upload-page">
      {/* Background */}
      <div className="hero-bg">
        <div className="grid-overlay" />
      </div>

      {/* Header */}
      <nav>
        <div className="nav-container">
          <button
            onClick={onBack}
            className="logo"
            style={{ background: "none", border: "none", cursor: "pointer" }}
          >
            <div className="logo-icon">
              {showAwsLogo ? (
                <img
                  src={AWS_LOGO_URL}
                  alt="AnalyzeOffer.ai"
                  onError={() => setLogoOk(false)}
                />
              ) : (
                <NavLogoSVG />
              )}
            </div>
            <span className="logo-text">
              AnalyzeOffer<span>.ai</span>
            </span>
          </button>

          <div className="nav-links">
            <button
              onClick={onBack}
              style={{
                background: "none",
                border: "none",
                color: "#94a3b8",
                cursor: "pointer",
                fontSize: "0.95rem",
              }}
            >
              ← Back to Home
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <section className="hero" style={{ paddingTop: "2rem" }}>
        <div className="hero-content" style={{ maxWidth: "600px" }}>
          <div className="badge">
            <span className="badge-dot" />
            AI-Powered Analysis
          </div>

          <h1 style={{ fontSize: "2.5rem" }}>Upload Your Document</h1>
          <p>
            Contracts, bills, quotes, statements, and reports. We'll analyze it
            and send you a detailed report.
          </p>

          {/* Upload Form */}
          <form onSubmit={handleSubmit} className="upload-form">
            {/* File Drop Zone */}
            <div
              className={`drop-zone ${dragActive ? "drag-active" : ""} ${files.length > 0 ? "has-file" : ""}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                type="file"
                id="file-input"
                onChange={handleFileChange}
                accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.txt"
                className="file-input"
                multiple
              />
              <label htmlFor="file-input" className="drop-zone-content">
                {files.length > 0 ? (
                  <>
                    <FileIconSVG />
                    <span className="file-name">{files.length} file{files.length > 1 ? 's' : ''} selected</span>
                    <span className="change-file">Click to add more (max 10)</span>
                  </>
                ) : (
                  <>
                    <UploadCloudIcon />
                    <span className="drop-text">
                      Drag and drop your files here
                    </span>
                    <span className="drop-subtext">or click to browse</span>
                    <span className="supported-formats">
                      PDF, Word, Images, TXT • Multiple files supported
                    </span>
                  </>
                )}
              </label>
            </div>

            {/* File List */}
            {files.length > 0 && (
              <div className="file-list">
                {files.map((file, index) => (
                  <div key={index} className="file-item">
                    <FileIconSVG />
                    <span className="file-item-name">{file.name}</span>
                    <span className="file-item-size">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </span>
                    <button
                      type="button"
                      className="file-remove"
                      onClick={() => removeFile(index)}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Document Type */}
            <div className="form-group">
              <label htmlFor="doc-type">Document Type</label>
              <div className="select-wrapper">
                <select
                  id="doc-type"
                  value={documentType}
                  onChange={(e) => setDocumentType(e.target.value)}
                  required
                >
                  <option value="">Select document type...</option>
                  {DOCUMENT_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
                <ChevronDownIcon />
              </div>
            </div>

            {/* Name & Email Row */}
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="full-name">Full Name</label>
                <input
                  type="text"
                  id="full-name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Your name"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                />
              </div>
            </div>

            {/* Notes */}
            <div className="form-group">
              <label htmlFor="notes">
                Additional Notes <span className="optional">(Optional)</span>
              </label>
              <textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any specific concerns or questions about this document?"
                rows={3}
              />
            </div>

            {/* Status Message */}
            {submitStatus && (
              <div className={`status-message ${submitStatus.type}`}>
                {submitStatus.type === "success" ? (
                  <CheckCircleIcon />
                ) : (
                  <AlertCircleIcon />
                )}
                {submitStatus.message}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting || files.length === 0}
              style={{ width: "100%", justifyContent: "center" }}
            >
              {isSubmitting ? (
                <>
                  <SpinnerIcon />
                  Analyzing...
                </>
              ) : (
                <>
                  <AnalyzeIcon />
                  Analyze Document
                </>
              )}
            </button>

            {/* Processing Time */}
            <p className="processing-note">
              <ClockIcon />
              Typical turnaround: 2-3 minutes
            </p>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer>
        <div className="footer-copy">
          © 2026 AnalyzeOffer.ai — Know More. Pay Less.
        </div>
      </footer>

      {/* Upload Page Specific Styles */}
      <style>{`
        .upload-page {
          min-height: 100vh;
          display: flex;
          flex-direction: column;
        }

        .upload-form {
          background: rgba(30, 41, 59, 0.5);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 16px;
          padding: 2rem;
          backdrop-filter: blur(10px);
          margin-top: 2rem;
          text-align: left;
        }

        .drop-zone {
          position: relative;
          border: 2px dashed rgba(74, 222, 128, 0.3);
          border-radius: 12px;
          padding: 2.5rem 1.5rem;
          text-align: center;
          transition: all 0.2s ease;
          background: rgba(74, 222, 128, 0.02);
          margin-bottom: 1.5rem;
          cursor: pointer;
        }

        .drop-zone:hover,
        .drop-zone.drag-active {
          border-color: #4ade80;
          background: rgba(74, 222, 128, 0.08);
        }

        .drop-zone.has-file {
          border-color: #4ade80;
          border-style: solid;
          background: rgba(74, 222, 128, 0.05);
        }

        .file-input {
          position: absolute;
          inset: 0;
          opacity: 0;
          cursor: pointer;
        }

        .drop-zone-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
          pointer-events: none;
        }

        .drop-zone-content svg {
          width: 48px;
          height: 48px;
          color: #4ade80;
          margin-bottom: 0.5rem;
        }

        .drop-text {
          font-size: 1rem;
          font-weight: 500;
          color: #e2e8f0;
        }

        .drop-subtext {
          font-size: 0.875rem;
          color: #64748b;
        }

        .supported-formats {
          font-size: 0.75rem;
          color: #475569;
          margin-top: 0.5rem;
        }

        .file-name {
          font-size: 1rem;
          font-weight: 600;
          color: #4ade80;
        }

        .file-size {
          font-size: 0.875rem;
          color: #64748b;
        }

        .change-file {
          font-size: 0.75rem;
          color: #4ade80;
          margin-top: 0.5rem;
        }

        .file-list {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          margin-bottom: 1.5rem;
          max-height: 200px;
          overflow-y: auto;
        }

        .file-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem 1rem;
          background: rgba(15, 23, 42, 0.6);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 8px;
        }

        .file-item svg {
          width: 20px;
          height: 20px;
          color: #4ade80;
          flex-shrink: 0;
        }

        .file-item-name {
          flex: 1;
          font-size: 0.875rem;
          color: #e2e8f0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .file-item-size {
          font-size: 0.75rem;
          color: #64748b;
          flex-shrink: 0;
        }

        .file-remove {
          background: rgba(239, 68, 68, 0.2);
          border: none;
          color: #ef4444;
          width: 24px;
          height: 24px;
          border-radius: 4px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.875rem;
          transition: all 0.2s;
          flex-shrink: 0;
        }

        .file-remove:hover {
          background: rgba(239, 68, 68, 0.4);
        }

        .form-group {
          margin-bottom: 1.25rem;
        }

        .form-group label {
          display: block;
          font-size: 0.875rem;
          font-weight: 500;
          color: #e2e8f0;
          margin-bottom: 0.5rem;
        }

        .form-group .optional {
          color: #64748b;
          font-weight: 400;
        }

        .form-group input,
        .form-group select,
        .form-group textarea {
          width: 100%;
          padding: 0.875rem 1rem;
          background: rgba(15, 23, 42, 0.6);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 8px;
          color: #f8fafc;
          font-size: 1rem;
          transition: all 0.2s ease;
          box-sizing: border-box;
        }

        .form-group input:focus,
        .form-group select:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: #4ade80;
          box-shadow: 0 0 0 3px rgba(74, 222, 128, 0.1);
        }

        .form-group input::placeholder,
        .form-group textarea::placeholder {
          color: #475569;
        }

        .select-wrapper {
          position: relative;
        }

        .select-wrapper select {
          appearance: none;
          padding-right: 2.5rem;
        }

        .select-wrapper svg {
          position: absolute;
          right: 1rem;
          top: 50%;
          transform: translateY(-50%);
          width: 20px;
          height: 20px;
          color: #64748b;
          pointer-events: none;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }

        @media (max-width: 540px) {
          .form-row {
            grid-template-columns: 1fr;
          }
        }

        .status-message {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 1rem;
          border-radius: 8px;
          margin-bottom: 1.25rem;
          font-size: 0.875rem;
        }

        .status-message svg {
          width: 20px;
          height: 20px;
          flex-shrink: 0;
        }

        .status-message.success {
          background: rgba(74, 222, 128, 0.1);
          border: 1px solid rgba(74, 222, 128, 0.2);
          color: #4ade80;
        }

        .status-message.error {
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.2);
          color: #ef4444;
        }

        .processing-note {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          margin: 1rem 0 0 0;
          font-size: 0.875rem;
          color: #64748b;
        }

        .processing-note svg {
          width: 16px;
          height: 16px;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .spinner {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
}

/* ========== ICONS ========== */

function UploadIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function DocumentIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  );
}

function NavLogoSVG() {
  return (
    <svg viewBox="0 0 40 40" fill="none" aria-hidden="true">
      <path d="M20 4L35 12V28L20 36L5 28V12L20 4Z" fill="#1e2a4a" />
      <rect x="12" y="14" width="16" height="3" rx="1" fill="#4ade80" />
      <rect x="12" y="19" width="16" height="3" rx="1" fill="#4ade80" />
      <rect x="12" y="24" width="16" height="3" rx="1" fill="#4ade80" />
    </svg>
  );
}

function FooterLogoSVG() {
  return (
    <svg viewBox="0 0 40 40" fill="none" aria-hidden="true">
      <path d="M20 4L35 12V28L20 36L5 28V12L20 4Z" fill="#4ade80" />
      <rect x="12" y="14" width="16" height="3" rx="1" fill="#1e2a4a" />
      <rect x="12" y="19" width="16" height="3" rx="1" fill="#1e2a4a" />
      <rect x="12" y="24" width="16" height="3" rx="1" fill="#1e2a4a" />
    </svg>
  );
}

function UploadCloudIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242" />
      <path d="M12 12v9" />
      <path d="m16 16-4-4-4 4" />
    </svg>
  );
}

function FileIconSVG() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" x2="8" y1="13" y2="13" />
      <line x1="16" x2="8" y1="17" y2="17" />
      <line x1="10" x2="8" y1="9" y2="9" />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function CheckCircleIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

function AlertCircleIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" x2="12" y1="8" y2="12" />
      <line x1="12" x2="12.01" y1="16" y2="16" />
    </svg>
  );
}

function AnalyzeIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
      <path d="M21 3v5h-5" />
    </svg>
  );
}

function SpinnerIcon() {
  return (
    <svg
      className="spinner"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}
